import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from '../Dashboard/index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark, faPlusCircle, faXmark } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/Modal";
import Pagination from "../../../components/Pagination";
import NoResultsFound from "../../../components/NoResultsFound";
import SearchBar from "../../../components/SearchBar";
import TableSkeleton from "../../../components/Skeleton";
import { addNewCustomer, editCustomer, getAllCustomers } from "../../../redux/features/customers/customersService";
import { useNavigate } from "react-router-dom";

const Roles = [{ id: '1', name: 'customer-specialist' }, { id: '2', name: 'sales-person' }, { id: '3', name: 'manager' }]

const Customer = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { isCustomersLoading, pageCount, customers } = useSelector((state) => state.customer.allCustomers);
    const { isEditingCustomer } = useSelector((state) => state.customer.editedCustomer);

    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone_number: '',
        whatsapp_number: '',
        role: ''
    });

    const [errorMessages, setErrorMessages] = useState({});
    const [disabledBtn, setBtnDisable] = useState(false);
    const [skip, setSkip] = useState(0);

    const limit = 10;  // Number of items per page
    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [initialCustomer, setInitialCustomer] = useState(null);
    const [editErrorMsgs, setEditErrorMsgs] = useState({
        name: '',
        customer_code: ''
    });
    const [selectedRole, setSelectedRole] = useState({
        id: '',
        name: ''
    });

    const [disabledEditBtn, setEditBtnDisable] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchAllCustomers = useCallback(() => {
        dispatch(getAllCustomers({
            skip: skip,
            limit,
            search: searchQuery,
            role_name: 'customer-specialist'
        }));
    }, [dispatch, skip, searchQuery]);

    useEffect(() => {
        fetchAllCustomers();
    }, [fetchAllCustomers]);

    // Edit customer
    const handleSelectedCustomerChange = (e) => {
        const { name, value } = e.target;
        setErrorMsg(null);
        setSelectedCustomer((prev) => ({
            ...prev,
            [name]: value,
        }));

        setEditErrorMsgs((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const isValidEditCustomer = () => {
        const errors = {};
        const trimmedName = selectedCustomer.name.trim();
        const trimmedCode = selectedCustomer.customer_code.trim();

        if (!trimmedName) {
            errors.name = "Name is required.";
        }
        if (!trimmedCode) {
            errors.customer_code = "Customer Code is required.";
        }
        if (Object.keys(errors).length > 0) {
            setEditErrorMsgs(errors);
            return false;
        } else {
            return true;
        }
    };

    function getChangedValues(selectedData) {
        const changedValues = {};
        for (const key in selectedData) {
            if (
                key !== 'id' &&
                selectedData.hasOwnProperty(key) &&
                selectedData[key] !== initialCustomer[key]
            ) {
                changedValues[key] = selectedData[key];
            }

            if (key === 'id') {
                changedValues[key] = selectedData[key];
            }
        }
        return changedValues;
    }

    const editCustomerDetails = async () => {
        if (isValidEditCustomer(selectedCustomer)) {
            const data = getChangedValues(selectedCustomer);
            const hasChanges = Object.keys(data).some(key => key !== 'id');

            if (!hasChanges) {
                setErrorMsg('There are no changes to update.');
            } else {
                setEditBtnDisable(true);
                try {
                    const response = await dispatch(editCustomer(data)).unwrap();
                    setEditBtnDisable(false);
                    setSelectedCustomer(null);
                    setInitialCustomer(null);
                    setModalOpen(false);
                    showToast('success', response.message);
                    fetchAllCustomers();
                }
                catch (error) {
                    setEditBtnDisable(false);
                    showToast('error', error.message);
                };
            }
        }
    };

    //Activate Customer
    const [isActivateModalOpen, setActivateModalOpen] = useState(false);
    const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);

    const closeModal = () => setActivateModalOpen(false);
    const closDeactivationModal = () => setDeactivationModalOpen(false);

    const handleYes = async () => {
        setActivateModalOpen(false);
        try {
            const response = await dispatch(editCustomer({ id: selectedCustomerId, status: true })).unwrap();
            setSelectedCustomerId(null);
            setInitialCustomer(null);
            showToast('success', response.message);
            fetchAllCustomers();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }
    const handleRejectYes = async () => {
        setDeactivationModalOpen(false);
        try {
            const response = await dispatch(editCustomer({ id: selectedCustomerId, status: false })).unwrap();
            setSelectedCustomerId(null);
            setInitialCustomer(null);
            showToast('success', response.message);
            fetchAllCustomers();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    // Pagination
    const handlePageChange = (pageNumber) => {
        setSkip(pageNumber - 1);
    };

    const openLedger = (customerId) => navigate(`/admin/customer/ledger/${customerId}`);
    const openNewCustomer = () => navigate('/admin/customer/create-customer');
    return (
        <div className={`card-section`}>
            <div className={`search-card`}>
                <div className={`search-card-header`}>
                    <h1 className={`form-title`}>Customers <FontAwesomeIcon icon={faPlusCircle} onClick={openNewCustomer} style={{cursor: 'pointer'}}/></h1>
                    <SearchBar onSearch={(query) => setSearchQuery(query)} />
                </div>
                <div className={`search-card-body`}>
                    {isCustomersLoading ?
                        <TableSkeleton columns={["Customer Code", "Full Name", "Email", "Status", "Actions"]} rowCount={10} height={20} />
                        : customers.length > 0 ?
                            <div className={`table-container`}>
                                <table className={`table`}>
                                    <thead className={`thead`}>
                                        <tr>
                                            <th className={`theadColumnOne`}>Customer Code</th>
                                            <th>Full Name</th>
                                            <th>Email</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`tbody`}>
                                        {customers?.map((customer) => (
                                            <tr key={customer.id}>
                                                <td className={`tdataColumnOne`}>
                                                    {customer.customer_code}
                                                </td>
                                                <td>
                                                    {customer.full_name}
                                                </td>
                                                <td>{customer.email}</td>
                                                <td style={{minWidth: '100px'}}>{customer.status ? "Active" : "Inactive"}</td>
                                                <td className={`actionsBtn`} style={{minWidth: '300px'}}> 
                                                    <button className={`btn-edit-label`} onClick={() => {
                                                        openLedger(customer.id)
                                                    }}>View Ledger</button>
                                                    <button className={`btn-edit-label`} onClick={() => {
                                                        setSelectedCustomer(customer);
                                                        setInitialCustomer(customer);
                                                        setEditBtnDisable(false);
                                                        setErrorMsg(null);
                                                        setEditErrorMsgs({
                                                            name: '',
                                                            customer_code: ''
                                                        })
                                                        setModalOpen(true);
                                                    }}>Edit</button>
                                                    <button
                                                        className={customer.status ? `btn-inactive` : `btn-active`}
                                                        onClick={() => {
                                                            setSelectedCustomerId(customer.id);
                                                            customer.status ? setDeactivationModalOpen(true) : setActivateModalOpen(true);
                                                        }
                                                        }>{customer.status ? 'Deactivate' : 'Acitvate'}</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            :
                            <NoResultsFound />
                    }
                </div>
                <div className={`search-card-footer`}>
                    {pageCount > 0 && (
                        <div className={styles.paginationContainer}>
                            <Pagination
                                currentPage={skip + 1}
                                totalPages={pageCount}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {isModalOpen &&
                <div className={`modal-overlay`}>
                    <div className={`modal-card`}>
                        <div className={`modal-header`}>
                            <h4 className={`modal-title`}>Edit Customer</h4>
                            <FontAwesomeIcon icon={faXmark} onClick={() => setModalOpen(false)} size="lg" cursor="pointer" />
                        </div>
                        <div className={`modal-body`}>
                            <div className={styles.formContainer}>
                                <form
                                    className={styles.coordinatorForm}
                                    noValidate
                                >
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Name<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='full_name'
                                                name='full_name'
                                                value={selectedCustomer.full_name || ''}
                                                onChange={handleSelectedCustomerChange}
                                                placeholder='Enter Customer Name'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.full_name && (
                                                <p className={`text-danger`}>{editErrorMsgs.full_name}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Email<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='email'
                                                name='email'
                                                value={selectedCustomer.email || ''}
                                                onChange={handleSelectedCustomerChange}
                                                placeholder='Enter Email'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.email && (
                                                <p className={`text-danger`}>{editErrorMsgs.email}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Phone Number<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='phone_number'
                                                name='phone_number'
                                                value={selectedCustomer.phone_number || ''}
                                                onChange={handleSelectedCustomerChange}
                                                placeholder='Enter Phone Number'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.phone_number && (
                                                <p className={`text-danger`}>{editErrorMsgs.phone_number}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                WhatsApp Number<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='whatsapp_number'
                                                name='whatsapp_number'
                                                value={selectedCustomer.whatsapp_number || ''}
                                                onChange={handleSelectedCustomerChange}
                                                placeholder='Enter WhatsApp Number'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.whatsapp_number && (
                                                <p className={`text-danger`}>{editErrorMsgs.whatsapp_number}</p>
                                            )}
                                            {errorMsg && (
                                                <p className={`text-danger`}>{errorMsg}</p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        className={`btn-primary`}
                                        type='button'
                                        disabled={disabledEditBtn}
                                        onClick={editCustomerDetails}
                                    >
                                        {isEditingCustomer ? <BeatLoader customer='#ffffff' /> : `Save`}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {isActivateModalOpen && (
                <Modal
                    title='Customer Activation'
                    onClose={closeModal}
                    onYes={handleYes}
                    isInput={false}
                    iconName={faCircleCheck}
                    bodyText={`Are you sure you want to activate the Customer?`}
                    customerType={`var(--green)`}
                />
            )}
            {isDeactivationModalOpen && (
                <Modal
                    title='Customer Deactivation'
                    onClose={closDeactivationModal}
                    onYes={handleRejectYes}
                    isInput={false}
                    iconName={faCircleXmark}
                    bodyText={`Are you sure you want to deactivate the Customer?`}
                    customerType={`var(--danger)`}
                />
            )}
        </div>
    );
};

export default Customer;
